import { type ReactElement, useEffect, useState } from 'react';
import { whitelistApi, type InviteCode } from '../../lib/presale-api';

export function WhitelistPage(): ReactElement {
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [redeemCode, setRedeemCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    whitelistApi
      .check()
      .then((data) => {
        setIsWhitelisted(data.whitelisted);
        if (data.whitelisted) {
          whitelistApi
            .listCodes()
            .then(setCodes)
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleGenerate = async () => {
    try {
      const newCodes = await whitelistApi.generateCodes(3);
      setCodes((prev) => [...newCodes, ...prev]);
      setMessage({ type: 'success', text: `Generated ${newCodes.length} invite codes` });
    } catch (err: unknown) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to generate codes',
      });
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    try {
      await whitelistApi.redeemCode(redeemCode.trim());
      setIsWhitelisted(true);
      setRedeemCode('');
      setMessage({ type: 'success', text: 'Invite code redeemed! You can now create presales.' });
    } catch (err: unknown) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to redeem code',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#c4f70e] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Launcher Whitelist</h1>
        <p className="mt-1 text-sm text-white/40">Invite-only access to create presales</p>
      </div>

      {message && (
        <div
          className={`rounded-lg p-3 text-sm ${message.type === 'success' ? 'border border-green-500/20 bg-green-500/10 text-green-400' : 'border border-red-500/20 bg-red-500/10 text-red-400'}`}
        >
          {message.text}
        </div>
      )}

      {!isWhitelisted ? (
        /* Redeem Section */
        <div className="notch-border-lg notch-border-white/8 space-y-4 p-6 [--notch-bg:rgba(255,255,255,0.02)]">
          <h2 className="text-lg font-semibold text-white">Redeem Invite Code</h2>
          <p className="text-sm text-white/40">
            Enter an invite code from an existing launcher to get whitelisted.
          </p>
          <div className="flex gap-3">
            <input
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              placeholder="ABCD1234"
              maxLength={8}
              className="flex-1 rounded-lg border border-white/[0.1] bg-white/[0.04] px-4 py-3 text-center font-mono tracking-widest text-white placeholder:text-white/20 focus:border-[#c4f70e]/50 focus:outline-none"
            />
            <button
              onClick={handleRedeem}
              disabled={!redeemCode.trim()}
              className="clip-notched bg-[#c4f70e] px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#c4f70e]/90 disabled:opacity-50"
            >
              Redeem
            </button>
          </div>
        </div>
      ) : (
        /* Launcher Section */
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg border border-[#c4f70e]/20 bg-[#c4f70e]/5 p-4">
            <span className="text-lg text-[#c4f70e]">✓</span>
            <span className="text-sm text-[#c4f70e]">You are a whitelisted launcher</span>
          </div>

          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Your Invite Codes</h2>
              <button
                onClick={handleGenerate}
                className="rounded-lg bg-white/[0.06] px-4 py-2 text-xs text-white/60 transition-colors hover:bg-white/[0.1]"
              >
                + Generate Codes
              </button>
            </div>
            {codes.length > 0 ? (
              <div className="space-y-2">
                {codes.map((code: InviteCode) => (
                  <div
                    key={code.id}
                    className="flex items-center justify-between border-b border-white/[0.04] py-2 last:border-0"
                  >
                    <span className="font-mono tracking-wider text-white">{code.code}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        code.status === 'ACTIVE'
                          ? 'bg-green-500/10 text-green-400'
                          : code.status === 'USED'
                            ? 'bg-white/10 text-white/40'
                            : 'bg-red-500/10 text-red-400'
                      }`}
                    >
                      {code.status}
                    </span>
                    {code.expiresAt && (
                      <span className="text-xs text-white/20">
                        exp {new Date(code.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-white/30">
                No codes generated yet. Click "Generate Codes" to create invite links.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
